import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  MinusCircle,
  XCircle,
} from "lucide-react";
import {
  useBotFilter,
  useCookieScan,
  useDestinations,
  useInboundSources,
  useTrackingConfig,
  type BotFilterSummary,
  type CookieScanResponse,
  type DestinationType,
  type TrackingConfig,
  type TrackingDestination,
} from "../lib/project-tracking";
import {
  useProjectCustomerLink,
  type ProjectDetail,
} from "../lib/project-detail";
import { useWebhookEndpoints } from "../lib/webhook-endpoints";
import { offersKundeLink, useDhCustomers } from "../lib/project-wizard";
import { useRules } from "../lib/rules";

/**
 * "Konfigurations-Status" — at-a-glance config checklist on the project Overview.
 * Aggregates the per-tab endpoints (tracking-config, destinations, cookie-scan,
 * bot-filter, inbound sources, webhook endpoints, customer-link, rules) into
 * grouped rows, each linking to the area that owns the setting. Read-only.
 */

type Tone = "ok" | "warn" | "crit" | "neutral";

interface StatusItem {
  label: string;
  tone: Tone;
  detail?: string;
  /** Relative route to the owning tab, e.g. "../consent". */
  to: string;
}

interface StatusGroup {
  title: string;
  /** null = data still loading for this group. */
  items: StatusItem[] | null;
}

const TONE_ICON: Record<Tone, typeof CheckCircle2> = {
  ok: CheckCircle2,
  warn: AlertTriangle,
  crit: XCircle,
  neutral: MinusCircle,
};

const TONE_COLOR: Record<Tone, string> = {
  ok: "text-success",
  warn: "text-warning",
  crit: "text-danger",
  neutral: "text-text-tertiary",
};

const DEST_TYPE_LABEL: Record<DestinationType, string> = {
  meta: "Meta",
  google: "Google (GA4)",
  google_ads: "Google Ads",
  linkedin: "LinkedIn",
  dhctrack: "DHC",
  custom: "Custom",
};

export function ProjectStatusChecklist({
  project,
  projectId,
}: {
  project: ProjectDetail;
  projectId: number;
}) {
  const trackingConfig = useTrackingConfig(projectId);
  const destinations = useDestinations(projectId);
  const cookieScan = useCookieScan(projectId);
  const botFilter = useBotFilter(projectId);
  const inboundSources = useInboundSources(projectId);
  const webhookEndpoints = useWebhookEndpoints(projectId);
  const customerLink = useProjectCustomerLink(projectId);
  const customers = useDhCustomers();
  const rules = useRules({ project: projectId });

  const kundeRecId = customerLink.data?.kundeRecId ?? null;
  const customerName =
    customers.data?.customers.find((c) => c.kundeRecId === kundeRecId)
      ?.company ?? null;

  const ruleItems = rules.data?.items ?? [];
  const globalRules = ruleItems.filter((r) => r.is_global).length;
  const projectRules = ruleItems.length - globalRules;

  const detectedSources = inboundSources.data?.sources.length ?? 0;
  const manualEndpoints =
    webhookEndpoints.data?.items.filter((e) => e.status === "active").length ??
    0;

  const groups: StatusGroup[] = [
    {
      title: "Erreichbarkeit & Einbau",
      items: buildReachability(project),
    },
    {
      title: "Datenquellen (rein)",
      items:
        inboundSources.data && webhookEndpoints.data
          ? buildSources(detectedSources, manualEndpoints)
          : null,
    },
    {
      title: "Consent & Recht",
      items: trackingConfig.data
        ? buildConsent(trackingConfig.data.config, cookieScan.data)
        : null,
    },
    {
      title: "Conversion-API (Meta · Google · LinkedIn)",
      items:
        trackingConfig.data && destinations.data
          ? buildConversionApi(
              destinations.data.destinations,
              trackingConfig.data.config,
            )
          : null,
    },
    {
      title: "Datenqualität",
      items: trackingConfig.data
        ? buildQuality(
            trackingConfig.data.config,
            botFilter.data,
            project.engagementTrackingEnabled,
            project.mergeReviewEnabled,
          )
        : null,
    },
    {
      title: "Verwaltung",
      items: buildAdmin(
        project,
        kundeRecId,
        customerName,
        globalRules,
        projectRules,
      ),
    },
  ];

  const allItems = groups.flatMap((g) => g.items ?? []);
  const critCount = allItems.filter((i) => i.tone === "crit").length;
  const warnCount = allItems.filter((i) => i.tone === "warn").length;

  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="overflow-hidden rounded-md border border-border-primary">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className={`flex w-full items-center justify-between bg-bg-secondary px-4 py-2.5 text-left transition-colors hover:bg-bg-hover ${
          collapsed ? "" : "border-b border-border-primary"
        }`}
      >
        <span className="flex items-center gap-2">
          {collapsed ? (
            <ChevronRight size={14} className="text-text-tertiary" />
          ) : (
            <ChevronDown size={14} className="text-text-tertiary" />
          )}
          <span className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
            Konfigurations-Status
          </span>
        </span>
        <StatusSummary crit={critCount} warn={warnCount} />
      </button>
      {!collapsed && (
        <div className="divide-y divide-border-secondary">
          {groups.map((g) => (
            <Group key={g.title} group={g} />
          ))}
        </div>
      )}
    </div>
  );
}

function StatusSummary({ crit, warn }: { crit: number; warn: number }) {
  if (crit === 0 && warn === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success">
        <CheckCircle2 size={13} /> Alles eingerichtet
      </span>
    );
  }
  return (
    <span className="flex items-center gap-3 text-xs font-medium">
      {crit > 0 && (
        <span className="inline-flex items-center gap-1.5 text-danger">
          <XCircle size={13} /> {crit} kritisch
        </span>
      )}
      {warn > 0 && (
        <span className="inline-flex items-center gap-1.5 text-warning">
          <AlertTriangle size={13} /> {warn} {warn === 1 ? "Hinweis" : "Hinweise"}
        </span>
      )}
    </span>
  );
}

function Group({ group }: { group: StatusGroup }) {
  const [open, setOpen] = useState(true);
  const items = group.items;
  const groupCrit = items?.filter((i) => i.tone === "crit").length ?? 0;
  const groupWarn = items?.filter((i) => i.tone === "warn").length ?? 0;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-4 py-2 text-left transition-colors hover:bg-bg-hover"
      >
        {open ? (
          <ChevronDown size={14} className="text-text-tertiary" />
        ) : (
          <ChevronRight size={14} className="text-text-tertiary" />
        )}
        <span className="font-mono text-[11px] uppercase tracking-wider text-text-secondary">
          {group.title}
        </span>
        {!open && (groupCrit > 0 || groupWarn > 0) && (
          <span className="ml-1 flex items-center gap-1.5">
            {groupCrit > 0 && (
              <span className="h-1.5 w-1.5 rounded-full bg-danger" />
            )}
            {groupWarn > 0 && (
              <span className="h-1.5 w-1.5 rounded-full bg-warning" />
            )}
          </span>
        )}
      </button>
      {open && (
        <div className="pb-1">
          {items === null ? (
            <Row item={{ label: "Lade…", tone: "neutral", to: "." }} muted />
          ) : (
            items.map((item) => <Row key={item.label} item={item} />)
          )}
        </div>
      )}
    </div>
  );
}

function Row({ item, muted }: { item: StatusItem; muted?: boolean }) {
  const Icon = TONE_ICON[item.tone];
  const inner = (
    <>
      <Icon size={15} className={`shrink-0 ${TONE_COLOR[item.tone]}`} />
      <span className="text-sm text-text-primary">{item.label}</span>
      {item.detail && (
        <span className="truncate font-mono text-xs text-text-tertiary">
          {item.detail}
        </span>
      )}
    </>
  );

  if (muted) {
    return (
      <div className="flex items-center gap-2.5 px-4 py-1.5 pl-10">{inner}</div>
    );
  }

  return (
    <Link
      to={item.to}
      relative="path"
      className="group flex items-center gap-2.5 px-4 py-1.5 pl-10 transition-colors hover:bg-bg-hover"
    >
      {inner}
      <ArrowRight
        size={14}
        className="ml-auto shrink-0 text-text-tertiary opacity-0 transition-opacity group-hover:opacity-100"
      />
    </Link>
  );
}

// ---- builders -------------------------------------------------------------
// Each returns the rows for one group from the relevant endpoint data.

function buildReachability(p: ProjectDetail): StatusItem[] {
  const items: StatusItem[] = [];

  items.push({
    label: p.active ? "Tracking aktiv" : "Tracking pausiert",
    tone: p.active ? "ok" : "warn",
    to: "../settings",
  });

  items.push(
    p.subdomain
      ? {
          label: "Subdomain registriert",
          tone: "ok",
          detail: `${p.subdomain}.dhc-track.io`,
          to: "../tracking",
        }
      : { label: "Keine Subdomain", tone: "warn", to: "../tracking" },
  );

  items.push(
    p.customHostname
      ? {
          label: "Custom Hostname gesetzt",
          tone: "ok",
          detail: p.customHostname,
          to: "../tracking",
        }
      : {
          label: "Custom Hostname nicht genutzt",
          tone: "neutral",
          to: "../tracking",
        },
  );

  items.push(
    p.allowedDomains.length > 0
      ? {
          label: "Erlaubte Domains gesetzt",
          tone: "ok",
          detail: `${p.allowedDomains.length} ${p.allowedDomains.length === 1 ? "Domain" : "Domains"}`,
          to: "../tracking",
        }
      : {
          label: "Keine erlaubten Domains — alles erlaubt",
          tone: "crit",
          to: "../tracking",
        },
  );

  const events24h = p.stats.last24h;
  items.push(
    events24h > 0
      ? {
          label: "Snippet liefert Events",
          tone: "ok",
          detail: `${events24h.toLocaleString("de-DE")} / 24 h`,
          to: "../tracking",
        }
      : {
          label: "Keine Events in 24 h",
          tone: p.active ? "warn" : "neutral",
          to: "../tracking",
        },
  );

  return items;
}

function buildSources(detected: number, manual: number): StatusItem[] {
  const total = detected + manual;
  if (total === 0) {
    return [
      { label: "Keine Integrationen aktiv", tone: "neutral", to: "../integrations" },
    ];
  }
  return [
    {
      label: `${total} ${total === 1 ? "Integration" : "Integrationen"} aktiv`,
      tone: "ok",
      detail: `${detected} erkannt · ${manual} manuell`,
      to: "../integrations",
    },
  ];
}

function buildConsent(
  cfg: TrackingConfig,
  scan: CookieScanResponse | undefined,
): StatusItem[] {
  const items: StatusItem[] = [];

  if (cfg.consent_mode === "banner") {
    items.push({ label: "Consent-Modus: DHC-Banner", tone: "ok", to: "../consent" });
  } else if (cfg.consent_mode === "externalCMP") {
    items.push({
      label: "Consent-Modus: externes Banner",
      tone: "ok",
      to: "../consent",
    });
  } else {
    items.push({
      label: "Consent-Modus: kein Banner",
      tone: "warn",
      detail: "Tracking findet ohne Einwilligung statt",
      to: "../consent",
    });
  }

  const stage = cfg.default_denied_stage;
  items.push({
    label: `Ablehnung: Stufe ${stage}`,
    tone: stage === "C" ? "ok" : stage === "B" ? "warn" : "crit",
    detail:
      stage === "C" ? "empfohlen" : stage === "A" ? "höchstes Risiko" : undefined,
    to: "../consent",
  });

  items.push({
    label: cfg.cookie_autoblock_enabled
      ? "Cookie-Auto-Block aktiv"
      : "Cookie-Auto-Block aus",
    tone: cfg.cookie_autoblock_enabled ? "ok" : "neutral",
    detail: cfg.cookie_autoblock_enabled ? undefined : "optional",
    to: "../consent",
  });

  const s = scan?.scan;
  if (!s || !s.scanned_at) {
    items.push({
      label: "Cookie-Scan: noch nie gelaufen",
      tone: "neutral",
      to: "../consent",
    });
  } else {
    const open = s.findings.length;
    items.push(
      open > 0
        ? {
            label: `Cookie-Scan: ${open} offene ${open === 1 ? "Fund" : "Funde"}`,
            tone: "warn",
            detail: relTime(s.scanned_at),
            to: "../consent",
          }
        : {
            label: "Cookie-Scan: sauber",
            tone: "ok",
            detail: relTime(s.scanned_at),
            to: "../consent",
          },
    );
  }

  return items;
}

export function buildConversionApi(
  dests: TrackingDestination[],
  cfg: TrackingConfig,
): StatusItem[] {
  const items: StatusItem[] = [];
  const active = dests.filter((d) => d.active);

  if (active.length === 0) {
    items.push({
      label: "Keine Conversion-API aktiv",
      tone: "warn",
      detail: "Meta · Google · LinkedIn",
      to: "../destinations",
    });
    return items;
  }

  items.push({
    label: `${active.length} Conversion-API aktiv`,
    tone: "ok",
    detail: active.map((d) => DEST_TYPE_LABEL[d.type]).join(" · "),
    to: "../destinations",
  });

  const withoutCreds = active.filter((d) => !d.has_credentials).length;
  items.push(
    withoutCreds === 0
      ? { label: "Token / Zugriff hinterlegt", tone: "ok", to: "../destinations" }
      : {
          label: `${withoutCreds} ${withoutCreds === 1 ? "Ziel" : "Ziele"} ohne eigenen Token`,
          tone: "neutral",
          detail: "ggf. System-User",
          to: "../destinations",
        },
  );

  // Consent gap: consent-dependent destinations that fire without a consent basis.
  const gapModes =
    cfg.consent_mode === "off" || cfg.consent_mode === "externalCMP";
  const consentDependent = active.filter(
    (d) => d.category === "marketing" || d.category === "statistics",
  );
  const gap = gapModes
    ? consentDependent.filter((d) => d.denied_stage !== "A")
    : [];
  if (gap.length > 0) {
    const n = `${gap.length} ${gap.length === 1 ? "Ziel" : "Ziele"}`;
    // External banner: targets legitimately wait for its consent, so only a soft hint to verify the hook-up.
    items.push(
      cfg.consent_mode === "externalCMP"
        ? {
            label: `${n} ${gap.length === 1 ? "wartet" : "warten"} auf die Zustimmung des externen Banners`,
            tone: "warn",
            detail: "Anbindung prüfen",
            to: "../consent",
          }
        : {
            label: `${n} ohne Einwilligungs-Basis`,
            tone: "crit",
            detail: "Consent-Modus prüfen",
            to: "../consent",
          },
    );
  } else if (consentDependent.length > 0) {
    items.push({
      label: "Consent-Basis der Ziele ok",
      tone: "ok",
      to: "../destinations",
    });
  }

  return items;
}

function buildQuality(
  cfg: TrackingConfig,
  bot: BotFilterSummary | undefined,
  advancedAnalytics: boolean,
  mergeReview: boolean,
): StatusItem[] {
  const items: StatusItem[] = [];

  const fp = cfg.fingerprint_mode;
  items.push({
    label:
      fp === "consent"
        ? "Fingerprinting: aktiv (mit Consent)"
        : fp === "always"
          ? "Fingerprinting: aktiv ohne Consent"
          : "Fingerprinting: aus",
    tone: fp === "consent" ? "ok" : fp === "always" ? "warn" : "neutral",
    detail: fp === "always" ? "einwilligungspflichtig" : undefined,
    to: "../quality",
  });

  if (!cfg.bot_gate_enabled) {
    items.push({ label: "Bot-Filter aus", tone: "neutral", to: "../quality" });
  } else if (bot && !bot.configured) {
    items.push({
      label: "Bot-Filter an — Keys fehlen",
      tone: "warn",
      detail: "Turnstile-Keys nicht gesetzt",
      to: "../quality",
    });
  } else {
    items.push({ label: "Bot-Filter aktiv", tone: "ok", to: "../quality" });
  }

  items.push({
    label: advancedAnalytics ? "Advanced Analytics aktiv" : "Advanced Analytics aus",
    tone: advancedAnalytics ? "ok" : "neutral",
    detail: advancedAnalytics ? undefined : "optional",
    to: "../quality",
  });

  items.push({
    label: mergeReview ? "Merge-Review aktiv" : "Merge-Review aus",
    tone: mergeReview ? "ok" : "neutral",
    to: "../quality",
  });

  return items;
}

function buildAdmin(
  p: ProjectDetail,
  kundeRecId: string | null,
  customerName: string | null,
  globalRules: number,
  projectRules: number,
): StatusItem[] {
  const totalRules = globalRules + projectRules;
  // Partner-workspace projects never get a dh_portal customer link (TASK-142),
  // so the checklist must not offer one — same guard as wizard + settings tab.
  const kundeItems: StatusItem[] = !offersKundeLink(p.workspace)
    ? []
    : kundeRecId
      ? [
          {
            label: "DHC-Kunde verknüpft",
            tone: "ok",
            detail: customerName ?? undefined,
            to: "../settings",
          },
        ]
      : [
          {
            label: "Kein DHC-Kunde verknüpft",
            tone: "neutral",
            detail: "optional",
            to: "../settings",
          },
        ];
  return [
    ...kundeItems,
    {
      label: p.bqSyncEnabled ? "BigQuery-Sync aktiv" : "BigQuery-Sync aus",
      tone: p.bqSyncEnabled ? "ok" : "neutral",
      to: "../settings",
    },
    totalRules > 0
      ? {
          label: `${totalRules} Approval-${totalRules === 1 ? "Rule" : "Rules"} aktiv`,
          tone: "ok",
          detail: `${globalRules} global · ${projectRules} projektspezifisch`,
          to: "../rules",
        }
      : {
          label: "Keine Approval-Rules",
          tone: "neutral",
          detail: "optional",
          to: "../rules",
        },
  ];
}

function relTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const days = Math.floor(diffMs / 86_400_000);
  if (days >= 1) return `vor ${days} ${days === 1 ? "Tag" : "Tagen"}`;
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours >= 1) return `vor ${hours} h`;
  const mins = Math.floor(diffMs / 60_000);
  return mins >= 1 ? `vor ${mins} min` : "gerade eben";
}
